import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useRestaurantContext } from "@/contexts/RestaurantContext";
import { toast } from "sonner";
import { 
  printCancellationToKitchen, 
  printCancellationReceipt, 
  getCancellationPrintSettings,
  type CancellationData,
} from "@/lib/cancellation-printing";
import { normalizeTableNumber } from "@/lib/utils";
import { safeRemoveChannel } from "@/lib/supabase-channel";
import { withRetry } from "@/lib/supabase-retry";
import { 
  saveActiveConsumption, 
  getActiveConsumption,
  hasPendingOpsForEntity,
  type ActiveConsumption,
} from "@/lib/offline-storage";
import { useSQLiteLocal } from "./useSQLiteLocal";
import { useSQLiteSync } from "./useSQLiteSync";
import { getIsOnline, getLastOnlineTransitionAt } from "@/lib/connectivity";
import { getCurrentOrder, cancelItemLocal, decreaseItemLocal } from "@/lib/dataGateway";

// Grace window (ms) after offline→online transition during which we
// skip phantom-clear branches to give useOfflineSync/useOfflineSyncExtended
// time to drain pending_orders/pending_items.
const RECONNECT_GRACE_MS = 3000;

export interface SentOrderItem {
  id: string;
  product_name: string;
  quantity: number;
  unit_price: number;
  total_price: number;
  notes: string | null;
  product_id?: string | null;
  combo_id?: string | null;
  combo_selections?: any;
  selected_addons?: any;
  created_by?: string | null;
}

export interface CancelledOrderItem {
  id: string;
  order_item_id: string;
  product_name: string;
  quantity: number;
  unit_price: number;
  total_price: number;
  notes: string | null;
  reason: string | null;
  cancelled_at: string;
}

// Shape do pedido ativo retornado pelo servidor local (GET .../current-order).
interface LocalCurrentOrder {
  id: string;
  order_number: number | null;
  table_number?: string | null;
  tab_number?: string | null;
  items: SentOrderItem[];
  cancellations: CancelledOrderItem[];
}

// Helper to generate table number variations for tolerant matching
function getTableNumberVariations(num: string): string[] {
  const normalized = normalizeTableNumber(num);
  const padded2 = normalized.padStart(2, "0");
  const padded3 = normalized.padStart(3, "0");
  const padded4 = normalized.padStart(4, "0");
  const variations = new Set([num, normalized, padded2, padded3, padded4]);
  return Array.from(variations);
}

const ACTIVE_ORDER_STATUSES = ["pending", "preparing", "ready"] as const;

export function useCurrentOrderItems(tableId?: string, commandId?: string) {
  const { activeRestaurant } = useRestaurantContext();
  const [sentItems, setSentItems] = useState<SentOrderItem[]>([]);
  const [cancelledItems, setCancelledItems] = useState<CancelledOrderItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [orderId, setOrderId] = useState<string | null>(null);
  const [orderNumber, setOrderNumber] = useState<number | null>(null);
  const [isCancelling, setIsCancelling] = useState(false);
  
  // Fail-safe state - track if last fetch failed to show warning
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [isUsingCache, setIsUsingCache] = useState(false);
  const lastSuccessfulFetchRef = useRef<Date | null>(null);
  const tableNumberRef = useRef<string | null>(null);
  const commandNumberRef = useRef<string | null>(null);

  // SQLite hooks for offline support
  const { 
    isReady: sqliteReady,
    getActiveConsumption: getLocalConsumption,
    upsertActiveConsumption,
    getTableByNumber,
    getCommandByNumber,
  } = useSQLiteLocal();
  const { isOnline } = useSQLiteSync();

  // Retry loop to mitigate race conditions when entering right after an order is created/linked.
  const retryTimeoutRef = useRef<number | null>(null);
  const retryAttemptRef = useRef(0);
  
  // Debounce ref for fetchOrderItems to prevent rapid successive calls causing flickering
  const fetchDebounceRef = useRef<number | null>(null);
  
  // Lightweight items-only refetch refs
  const itemsDebounceRef = useRef<number | null>(null);
  
  // Shared fetch ID ref for race condition protection (used by both heavy and light fetches)
  const fetchIdRef = useRef(0);
  
  // Ref to track if we had items before (avoids sentItems.length in deps)
  const hadItemsRef = useRef(false);

  // Fetch from local SQLite cache (offline fallback)
  const fetchFromLocalCache = useCallback((options?: { skipLinkValidation?: boolean }): { orderId: string; orderNumber: number | null; items: SentOrderItem[] } | null => {
    if (!sqliteReady) return null;

    const entityId = tableId || commandId;
    if (!entityId) return null;

    const shouldValidateLink = options?.skipLinkValidation !== true;

    // Validate link only when requested.
    // In offline/network-error fallback we intentionally trust cache to preserve visibility.
    if (shouldValidateLink) {
      if (tableId && tableNumberRef.current) {
        const localTable = getTableByNumber(tableNumberRef.current);
        if (localTable && !localTable.current_order_id && !(localTable as any).grouped_to_order_id) {
          console.log(`[useCurrentOrderItems] Table ${tableNumberRef.current} has no current_order_id in local DB, ignoring cache`);
          return null;
        }
      }

      if (commandId && commandNumberRef.current) {
        const localCommand = getCommandByNumber(commandNumberRef.current);
        if (localCommand && !localCommand.current_order_id && !(localCommand as any).grouped_to_order_id) {
          console.log(`[useCurrentOrderItems] Command ${commandNumberRef.current} has no current_order_id in local DB, ignoring cache`);
          return null;
        }
      }
    }

    const cached = getLocalConsumption(entityId);
    if (cached) {
      console.log(`[useCurrentOrderItems] Found cached consumption for ${tableId ? 'table' : 'command'}: ${cached.items.length} items`);
      return {
        orderId: cached.orderId,
        orderNumber: cached.orderNumber,
        items: cached.items.map(item => ({
          id: item.id,
          product_name: item.product_name,
          quantity: item.quantity,
          unit_price: item.unit_price,
          total_price: item.total_price,
          notes: item.notes,
          product_id: item.product_id,
          combo_id: item.combo_id,
        })),
      };
    }

    return null;
  }, [sqliteReady, tableId, commandId, getLocalConsumption, getTableByNumber, getCommandByNumber]);

  // Cache items to local SQLite
  const cacheToLocal = useCallback((
    entityId: string, 
    entityType: 'table' | 'command', 
    orderId: string, 
    orderNumber: number | null, 
    number: string, 
    items: SentOrderItem[]
  ) => {
    if (!sqliteReady) return;
    
    const localItems = items.map(item => ({
      id: item.id,
      order_id: orderId,
      product_id: item.product_id || null,
      combo_id: item.combo_id || null,
      product_name: item.product_name,
      quantity: item.quantity,
      unit_price: item.unit_price,
      total_price: item.total_price,
      notes: item.notes,
      status: 'sent',
      sent_to_kitchen: 1,
      created_at: new Date().toISOString(),
      synced: 1,
      server_id: item.id,
    }));
    
    upsertActiveConsumption(entityId, entityType, orderId, orderNumber, number, localItems);
  }, [sqliteReady, upsertActiveConsumption]);

  const fetchOrderItems = useCallback(async (): Promise<string | null> => {
    // Clear error on new fetch attempt
    setFetchError(null);
    // Race condition protection: increment BEFORE any async work
    const fetchId = ++fetchIdRef.current;
    
    if (!tableId && !commandId) {
      setSentItems([]);
      setCancelledItems([]);
      setOrderId(null);
      return null;
    }

    if (!activeRestaurant) {
      setSentItems([]);
      setCancelledItems([]);
      setOrderId(null);
      return null;
    }

    // If offline, try local cache first
    if (!isOnline) {
      console.log('[useCurrentOrderItems] Offline mode - fetching from local cache');
      setIsLoading(true);
      setIsUsingCache(true);
      
      try {
        const cached = fetchFromLocalCache({ skipLinkValidation: true });
        if (cached) {
          setOrderId(cached.orderId);
          setOrderNumber(cached.orderNumber);
          setSentItems(cached.items);
          console.log(`[useCurrentOrderItems] Loaded ${cached.items.length} items from offline cache`);
          return cached.orderId;
        } else {
          // No cache available, show error
          setFetchError('Dados não disponíveis offline');
          console.warn('[useCurrentOrderItems] No offline cache available for this consumption');
          return null;
        }
      } finally {
        setIsLoading(false);
      }
    }

    // FAST-PATH (local-first): tenta o servidor local. Só assume o caso POSITIVO (pedido ativo);
    // 'none'/'unavailable' caem para a LÓGICA DE NUVEM ABAIXO (com phantom-clear/grace/pending-ops
    // intactas — não substituímos as guardas). Erro de negócio (4xx) propaga sem fallback.
    {
      const resource: "tables" | "commands" = tableId ? "tables" : "commands";
      const resourceId = (tableId || commandId) as string;
      setIsLoading(true);
      const local = await getCurrentOrder<LocalCurrentOrder>(resource, resourceId);
      if (local.kind === "businessError") {
        setFetchError("Erro ao carregar itens do pedido");
        setIsLoading(false);
        return orderId;
      }
      if (local.kind === "order") {
        if (fetchId !== fetchIdRef.current) {
          setIsLoading(false);
          return local.order.id;
        }
        const o = local.order;
        if (tableId && o.table_number) tableNumberRef.current = o.table_number;
        if (commandId && o.tab_number) commandNumberRef.current = o.tab_number;
        setOrderId(o.id);
        setOrderNumber(o.order_number);
        setSentItems(o.items || []);
        setCancelledItems(o.cancellations || []);
        hadItemsRef.current = (o.items?.length || 0) > 0;
        lastSuccessfulFetchRef.current = new Date();
        setIsUsingCache(false);
        if (import.meta.env.DEV) console.debug("[useCurrentOrderItems] fonte: local");
        try {
          if (tableId && o.items && tableNumberRef.current) {
            cacheToLocal(tableId, "table", o.id, o.order_number, tableNumberRef.current, o.items);
          } else if (commandId && o.items && commandNumberRef.current) {
            cacheToLocal(commandId, "command", o.id, o.order_number, commandNumberRef.current, o.items);
          }
        } catch {
          /* cache local não-crítico */
        }
        setIsLoading(false);
        return o.id;
      }
      // 'none' | 'unavailable' → segue para a lógica de nuvem abaixo (guardas preservadas)
    }

    setIsLoading(true);
    setIsUsingCache(false);
    try {
      let foundOrderId: string | null = null;
      let foundOrderNumber: number | null = null;
      let hadLookupError = false;

      if (tableId) {
        // PRIORITY 1: Use current_order_id from the table (most reliable)
        // Use retry to handle transient network errors
        const tableData = await withRetry(async () => {
          const { data, error } = await supabase
            .from("tables")
            .select("number, current_order_id, grouped_to_order_id, status")
            .eq("id", tableId)
            .eq("restaurant_id", activeRestaurant.id)
            .single();
          if (error) throw error;
          return data;
        }, { maxRetries: 2, baseDelayMs: 300 }).catch((error) => {
          hadLookupError = true;
          console.warn('[useCurrentOrderItems] Failed to resolve table link online, will try cache fallback:', error);
          return null;
        });

        if (tableData?.number) tableNumberRef.current = tableData.number;
        // Se a mesa já está disponível no servidor, limpar tudo e sair
        if (tableData?.status === 'available' && !tableData?.current_order_id) {
          // Grace window: right after reconnect, the sync queue may still be
          // draining. Avoid clearing items prematurely.
          const sinceOnline = Date.now() - getLastOnlineTransitionAt();
          if (sinceOnline < RECONNECT_GRACE_MS) {
            console.log(
              `[useCurrentOrderItems] Too soon after reconnect (${sinceOnline}ms), skipping phantom clear`
            );
            setIsLoading(false);
            return orderId;
          }
          // Guard: if there are pending offline ops for this table, DO NOT clear.
          // The server says "available" but offline work hasn't synced yet.
          const hasPending = await hasPendingOpsForEntity(activeRestaurant.id, {
            tableId,
            tableNumber: tableData?.number || tableNumberRef.current || undefined,
          });
          if (hasPending) {
            console.log(
              `[useCurrentOrderItems] Skipping phantom clear - pending offline ops exist for table ${tableData?.number || tableId}`
            );
            setIsLoading(false);
            return orderId;
          }
          console.log('[useCurrentOrderItems] Table already available on server - clearing phantom items');
          hadItemsRef.current = false;
          setSentItems([]);
          setCancelledItems([]);
          setOrderId(null);
          setOrderNumber(null);
          setIsLoading(false);
          return null;
        }
        // Use current_order_id, or fallback to grouped_to_order_id for grouped tables
        const tableOrderId = tableData?.current_order_id || tableData?.grouped_to_order_id;
        if (tableOrderId) {
          // Verify the order exists and is in an active status
          const { data: orderCheck } = await supabase
            .from("orders")
            .select("id, status, order_number")
            .eq("id", tableOrderId)
            .eq("restaurant_id", activeRestaurant.id)
            .in("status", [...ACTIVE_ORDER_STATUSES])
            .maybeSingle();

          if (orderCheck) {
            foundOrderId = orderCheck.id;
            foundOrderNumber = orderCheck.order_number;
            console.log("[useCurrentOrderItems] Found order via current_order_id:", foundOrderId);
          }
        }

        // FALLBACK: Search by table_number with variations (tolerant to "8" vs "08")
        if (!foundOrderId && tableData?.number) {
          const variations = getTableNumberVariations(tableData.number);
          console.log("[useCurrentOrderItems] Fallback: searching by table_number variations:", variations);

          const { data: orderData } = await supabase
            .from("orders")
            .select("id, order_number")
            .eq("restaurant_id", activeRestaurant.id)
            .in("table_number", variations)
            .in("status", [...ACTIVE_ORDER_STATUSES])
            .order("created_at", { ascending: false })
            .limit(1)
            .maybeSingle();

          if (orderData) {
            foundOrderId = orderData.id;
            foundOrderNumber = orderData.order_number;
            console.log("[useCurrentOrderItems] Found order via table_number fallback:", foundOrderId);
          }
        }
      } else if (commandId) {
        // PRIORITY 1: Use current_order_id from the command
        const { data: commandData } = await supabase
          .from("commands")
          .select("number, current_order_id, grouped_to_order_id")
          .eq("id", commandId)
          .eq("restaurant_id", activeRestaurant.id)
          .single();

        if (commandData?.number) commandNumberRef.current = commandData.number;
        // Use current_order_id, or fallback to grouped_to_order_id for grouped commands
        const commandOrderId = commandData?.current_order_id || commandData?.grouped_to_order_id;
        if (commandOrderId) {
          const { data: orderCheck } = await supabase
            .from("orders")
            .select("id, status, order_number")
            .eq("id", commandOrderId)
            .eq("restaurant_id", activeRestaurant.id)
            .in("status", [...ACTIVE_ORDER_STATUSES])
            .maybeSingle();

          if (orderCheck) {
            foundOrderId = orderCheck.id;
            foundOrderNumber = orderCheck.order_number;
            console.log("[useCurrentOrderItems] Found order via command current_order_id:", foundOrderId);
          }
        }

        // FALLBACK: Search by tab_number
        if (!foundOrderId && commandData?.number) {
          const { data: orderData } = await supabase
            .from("orders")
            .select("id, order_number")
            .eq("restaurant_id", activeRestaurant.id)
            .eq("tab_number", commandData.number)
            .in("status", [...ACTIVE_ORDER_STATUSES])
            .order("created_at", { ascending: false })
            .limit(1)
            .maybeSingle();

          if (orderData) {
            foundOrderId = orderData.id;
            foundOrderNumber = orderData.order_number;
            console.log("[useCurrentOrderItems] Found order via tab_number fallback:", foundOrderId);
          }
        }
      }

      if (!foundOrderId) {
        // Network error masked by .catch(() => null) may look like "no order"
        // Try strict local cache first
        const cached = fetchFromLocalCache();
        if (cached) {
          console.log('[useCurrentOrderItems] No online data - using local cache');
          setOrderId(cached.orderId);
          setOrderNumber(cached.orderNumber);
          setSentItems(cached.items);
          setIsUsingCache(true);
          setFetchError('Usando dados em cache (sem conexão)');
          return cached.orderId;
        }

        // If online lookup failed (or browser is now offline), relax link validation and trust local cache
        if (hadLookupError || !getIsOnline()) {
          const relaxedCached = fetchFromLocalCache({ skipLinkValidation: true });
          if (relaxedCached) {
            console.log('[useCurrentOrderItems] Online lookup failed - using relaxed local cache fallback');
            setOrderId(relaxedCached.orderId);
            setOrderNumber(relaxedCached.orderNumber);
            setSentItems(relaxedCached.items);
            setIsUsingCache(true);
            setFetchError('Usando dados em cache (sem conexão)');
            return relaxedCached.orderId;
          }
        }

        console.log("[useCurrentOrderItems] No active order found for table/command");
        // Only clear if we don't have previous data (fail-safe: keep last known state)
        if (!hadItemsRef.current) {
          setSentItems([]);
          setCancelledItems([]);
        }
        setOrderId(null);
        setOrderNumber(null);
        return null;
      }

      setOrderId(foundOrderId);
      setOrderNumber(foundOrderNumber);

      // Fetch order items and cancelled items in parallel with retry
      const [itemsResult, cancellationsResult] = await Promise.all([
        withRetry(async () => {
          const result = await supabase
            .from("order_items")
            .select("id, product_id, combo_id, combo_selections, selected_addons, product_name, quantity, unit_price, total_price, notes, created_at")
            .eq("order_id", foundOrderId)
            .order("created_at", { ascending: true });
          if (result.error) throw result.error;
          return result;
        }, { maxRetries: 2, baseDelayMs: 300 }),
        withRetry(async () => {
          const result = await supabase
            .from("order_item_cancellations")
            .select("id, order_item_id, product_name, quantity, unit_price, total_price, notes, reason, cancelled_at")
            .eq("order_id", foundOrderId)
            .order("cancelled_at", { ascending: true });
          if (result.error) throw result.error;
          return result;
        }, { maxRetries: 2, baseDelayMs: 300 }),
      ]);
      // Race condition protection: ignore stale heavy-fetch responses
      if (fetchId !== fetchIdRef.current) {
        console.log("[useCurrentOrderItems] Ignoring stale fetchOrderItems response");
        return foundOrderId;
      }
      setSentItems(itemsResult.data || []);
      setCancelledItems(cancellationsResult.data || []);
      lastSuccessfulFetchRef.current = new Date();
      console.log("[useCurrentOrderItems] Loaded", itemsResult.data?.length || 0, "items");

      // Cache to local SQLite for offline access (non-critical)
      try {
        if (tableId && itemsResult.data && tableNumberRef.current) {
          cacheToLocal(tableId, 'table', foundOrderId, foundOrderNumber, tableNumberRef.current, itemsResult.data);
        } else if (commandId && itemsResult.data && commandNumberRef.current) {
          cacheToLocal(commandId, 'command', foundOrderId, foundOrderNumber, commandNumberRef.current, itemsResult.data);
        }
      } catch (cacheError) {
        console.warn("[useCurrentOrderItems] Failed to cache locally (non-critical):", cacheError);
      }

      return foundOrderId;
    } catch (error) {
      console.error("[useCurrentOrderItems] Error:", error);
      
      // FAIL-SAFE: Try relaxed local cache first before giving up
      const cached = fetchFromLocalCache({ skipLinkValidation: true });
      if (cached) {
        console.log('[useCurrentOrderItems] Network error - using local cache');
        setOrderId(cached.orderId);
        setOrderNumber(cached.orderNumber);
        setSentItems(cached.items);
        setIsUsingCache(true);
        setFetchError('Usando dados em cache (sem conexão)');
        return cached.orderId;
      }
      
      // No cache available - keep last known state if we have items
      const errorMessage = error instanceof Error ? error.message : "Erro de conexão";
      setFetchError(errorMessage);
      
      // Log but don't clear data
      console.warn("[useCurrentOrderItems] Network error - keeping last known state. hadItems:", hadItemsRef.current);
      
      // If we have an orderId already, keep it (don't reset state)
      return orderId;
    } finally {
      setIsLoading(false);
    }
  }, [tableId, commandId, activeRestaurant, orderId, isOnline, fetchFromLocalCache, cacheToLocal]);

  // Debounced fetch to prevent flickering from multiple rapid realtime triggers
  const debouncedFetchOrderItems = useCallback(() => {
    if (fetchDebounceRef.current) {
      window.clearTimeout(fetchDebounceRef.current);
    }
    fetchDebounceRef.current = window.setTimeout(() => {
      fetchDebounceRef.current = null;
      fetchOrderItems();
    }, 300);
  }, [fetchOrderItems]);

  // Lightweight refetch: only fetches items for the known orderId (no order discovery)
  const refetchItemsOnly = useCallback(async () => {
    if (!orderId) return;
    
    const fetchId = ++fetchIdRef.current;
    
    try {
      const { data, error } = await supabase
        .from("order_items")
        .select("id, product_id, combo_id, combo_selections, selected_addons, product_name, quantity, unit_price, total_price, notes, created_at")
        .eq("order_id", orderId)
        .order("created_at", { ascending: true });
      
      if (error) throw error;
      
      // Ignore stale response if a newer fetch was triggered
      if (fetchId !== fetchIdRef.current) {
        console.log("[useCurrentOrderItems] Ignoring stale refetchItemsOnly response");
        return;
      }
      
      if (data) {
        setSentItems(data);
      }
    } catch (err) {
      // Silently fail - keep current state (fail-safe)
      console.warn("[useCurrentOrderItems] refetchItemsOnly failed, keeping current state");
    }
  }, [orderId]);

  // Debounced lightweight refetch for realtime order_items events
  const debouncedRefetchItems = useCallback(() => {
    if (itemsDebounceRef.current) {
      window.clearTimeout(itemsDebounceRef.current);
    }
    itemsDebounceRef.current = window.setTimeout(() => {
      itemsDebounceRef.current = null;
      refetchItemsOnly();
    }, 500);
  }, [refetchItemsOnly]);

  // Keep hadItemsRef in sync (avoids sentItems.length in fetchOrderItems deps)
  useEffect(() => {
    hadItemsRef.current = sentItems.length > 0;
  }, [sentItems.length]);

  // Cancel/delete a sent item
  // skipReceiptPrint: when true, skip printing the cashier receipt (used for batch printing)
  const cancelSentItem = useCallback(async (itemId: string, reason?: string, skipReceiptPrint?: boolean) => {
    if (!orderId || !activeRestaurant) return false;

    setIsCancelling(true);
    try {
      // Get current user and profile
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error("Usuário não autenticado");
        return false;
      }

      // Get user profile for name
      const { data: profileData } = await supabase
        .from("profiles")
        .select("full_name")
        .eq("id", user.id)
        .single();
      
      const cancelledByName = profileData?.full_name || user.email?.split("@")[0] || "Operador";

      // Get the item before deleting for logging purposes
      const itemToDelete = sentItems.find(item => item.id === itemId);
      if (!itemToDelete) {
        toast.error("Item não encontrado");
        return false;
      }

      // Get order info for table/command number
      const { data: orderInfo } = await supabase
        .from("orders")
        .select("table_number, tab_number")
        .eq("id", orderId)
        .single();

      // === FAST-PATH: cancelamento (INSERT cancelamento + DELETE item + recalcula totais,
      //     atômico) no servidor local (gateway) → a nuvem recebe via sync. Sucesso = early-return;
      //     o bloco de nuvem abaixo fica 100% intacto como fallback. KDS (cloud-direct, no-op no
      //     piloto) e impressão (client-side) seguem aqui.
      {
        const local = await cancelItemLocal(itemId, { reason: reason ?? null, cancelled_by: user.id });
        if (local.kind === "ok") {
          if (import.meta.env.DEV) console.debug("[cancelSentItem] cancelado no servidor local (nuvem via sync)");
          // KDS cloud-direct (igual à nuvem; no-op no piloto)
          await supabase
            .from("kds_ticket_items")
            .update({
              status: 'cancelled',
              done_at: new Date().toISOString(),
              notes_snapshot: `CANCELADO: ${reason || 'Sem motivo'} (por ${cancelledByName})`,
              updated_at: new Date().toISOString()
            } as any)
            .eq("order_item_id", itemId);

          setSentItems(prev => prev.filter(item => item.id !== itemId));

          // Impressão de cancelamento (background, não bloqueia) — mesmos dados da nuvem.
          const cancellationData: CancellationData = {
            productName: itemToDelete.product_name,
            productId: itemToDelete.product_id,
            quantity: itemToDelete.quantity,
            unitPrice: Number(itemToDelete.unit_price),
            totalPrice: Number(itemToDelete.total_price),
            reason: reason,
            tableNumber: orderInfo?.table_number || undefined,
            tabNumber: orderInfo?.tab_number || undefined,
            cancelledBy: cancelledByName,
            restaurantId: activeRestaurant.id,
            restaurantName: activeRestaurant.name,
          };
          getCancellationPrintSettings(activeRestaurant.id).then(async (settings) => {
            if (settings.printKitchen) await printCancellationToKitchen(cancellationData);
            if (settings.printReceipt && !skipReceiptPrint) await printCancellationReceipt(cancellationData);
          }).catch(err => console.error("Error printing cancellation:", err));

          debouncedRefetchItems();
          toast.success(`${itemToDelete.product_name} cancelado`);
          return true;
        }
        // 'unavailable' | 'businessError' (rolled back) → cai para o caminho de nuvem abaixo
      }

      // Save the cancellation record
      const { error: cancellationError } = await supabase
        .from("order_item_cancellations")
        .insert({
          restaurant_id: activeRestaurant.id,
          order_id: orderId,
          order_item_id: itemId,
          product_id: itemToDelete.product_id || null,
          product_name: itemToDelete.product_name,
          quantity: itemToDelete.quantity,
          unit_price: itemToDelete.unit_price,
          total_price: itemToDelete.total_price,
          notes: itemToDelete.notes,
          reason: reason || null,
          cancelled_by: user.id,
          created_by: itemToDelete.created_by || null,
        });

      if (cancellationError) {
        console.error("Error saving cancellation:", cancellationError);
        toast.error("Erro ao registrar cancelamento");
        return false;
      }

      // Mark KDS items as cancelled before deleting the order_item
      await supabase
        .from("kds_ticket_items")
        .update({
          status: 'cancelled',
          done_at: new Date().toISOString(),
          notes_snapshot: `CANCELADO: ${reason || 'Sem motivo'} (por ${cancelledByName})`,
          updated_at: new Date().toISOString()
        } as any)
        .eq("order_item_id", itemId);

      // Delete the order item and verify it was actually deleted
      const { data: deletedRows, error: deleteError } = await supabase
        .from("order_items")
        .delete()
        .eq("id", itemId)
        .select("id");

      if (deleteError) {
        console.error("Error deleting order item:", deleteError);
        toast.error("Erro ao cancelar item");
        return false;
      }

      if (!deletedRows || deletedRows.length === 0) {
        console.error("DELETE returned 0 rows - item may be referenced by other tables");
        toast.error("Erro ao cancelar item - item vinculado a outros registros");
        return false;
      }

      // Immediately remove from local state so UI updates instantly
      setSentItems(prev => prev.filter(item => item.id !== itemId));

      // Update order subtotal and total
      const { data: orderData } = await supabase
        .from("orders")
        .select("subtotal, total, discount, additional_fees, couvert_amount, payment_fee")
        .eq("id", orderId)
        .single();

      if (orderData) {
        const newSubtotal = Number(orderData.subtotal) - Number(itemToDelete.total_price);
        const newTotal = newSubtotal - Number(orderData.discount || 0) + 
                        Number(orderData.additional_fees || 0) + 
                        Number(orderData.couvert_amount || 0) +
                        Number(orderData.payment_fee || 0);

        await supabase
          .from("orders")
          .update({ 
            subtotal: Math.max(0, newSubtotal), 
            total: Math.max(0, newTotal) 
          })
          .eq("id", orderId);
      }

      // Print cancellation (async, don't wait)
      const cancellationData: CancellationData = {
        productName: itemToDelete.product_name,
        productId: itemToDelete.product_id,
        quantity: itemToDelete.quantity,
        unitPrice: Number(itemToDelete.unit_price),
        totalPrice: Number(itemToDelete.total_price),
        reason: reason,
        tableNumber: orderInfo?.table_number || undefined,
        tabNumber: orderInfo?.tab_number || undefined,
        cancelledBy: cancelledByName,
        restaurantId: activeRestaurant.id,
        restaurantName: activeRestaurant.name,
      };

      // Get print settings and print
      getCancellationPrintSettings(activeRestaurant.id).then(async (settings) => {
        if (settings.printKitchen) {
          await printCancellationToKitchen(cancellationData);
        }
        // Only print receipt if not skipped (for batch printing later)
        if (settings.printReceipt && !skipReceiptPrint) {
          await printCancellationReceipt(cancellationData);
        }
      }).catch(err => {
        console.error("Error printing cancellation:", err);
      });

      // Trigger a refetch to ensure full synchronization
      debouncedRefetchItems();

      toast.success(`${itemToDelete.product_name} cancelado`);
      return true;
    } catch (error) {
      console.error("Error cancelling item:", error);
      toast.error("Erro ao cancelar item");
      return false;
    } finally {
      setIsCancelling(false);
    }
  }, [orderId, sentItems, activeRestaurant, debouncedRefetchItems]);

  // Decrease item quantity (partial cancellation)
  const decreaseItemQuantity = useCallback(async (itemId: string, decreaseBy: number = 1): Promise<boolean> => {
    if (!orderId || !activeRestaurant) return false;

    const item = sentItems.find(i => i.id === itemId);
    if (!item) {
      toast.error("Item não encontrado");
      return false;
    }

    // If decreasing by all quantity, just cancel the whole item
    if (decreaseBy >= item.quantity) {
      return cancelSentItem(itemId, "Quantidade zerada no checkout");
    }

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error("Usuário não autenticado");
        return false;
      }

      const cancelledUnitPrice = Number(item.unit_price);
      const cancelledTotalPrice = cancelledUnitPrice * decreaseBy;

      // === FAST-PATH: decrease (INSERT cancelamento parcial + UPDATE qtd/total do item + recalcula
      //     totais, atômico) no servidor local (gateway) → a nuvem recebe via sync. Sucesso =
      //     early-return; o bloco de nuvem abaixo fica 100% intacto como fallback.
      {
        const local = await decreaseItemLocal(itemId, {
          quantity: decreaseBy,
          reason: "Ajuste de quantidade no checkout",
          cancelled_by: user.id,
        });
        if (local.kind === "ok") {
          if (import.meta.env.DEV) console.debug("[decreaseItemQuantity] decrementado no servidor local (nuvem via sync)");
          const newQuantity = item.quantity - decreaseBy;
          const newTotalPrice = cancelledUnitPrice * newQuantity;
          setSentItems(prev =>
            prev.map(i =>
              i.id === itemId ? { ...i, quantity: newQuantity, total_price: newTotalPrice } : i
            )
          );
          toast.success(`Quantidade de ${item.product_name} reduzida`);
          return true;
        }
        // 'unavailable' | 'businessError' (rolled back) → cai para o caminho de nuvem abaixo
      }

      // Create partial cancellation record
      const { error: cancellationError } = await supabase
        .from("order_item_cancellations")
        .insert({
          restaurant_id: activeRestaurant.id,
          order_id: orderId,
          order_item_id: itemId,
          product_id: item.product_id || null,
          product_name: item.product_name,
          quantity: decreaseBy,
          unit_price: cancelledUnitPrice,
          total_price: cancelledTotalPrice,
          notes: item.notes,
          reason: "Ajuste de quantidade no checkout",
          cancelled_by: user.id,
          created_by: item.created_by || null,
        });

      if (cancellationError) {
        console.error("Error saving partial cancellation:", cancellationError);
        toast.error("Erro ao registrar cancelamento parcial");
        return false;
      }

      // Update item quantity
      const newQuantity = item.quantity - decreaseBy;
      const newTotalPrice = cancelledUnitPrice * newQuantity;

      const { error: updateError } = await supabase
        .from("order_items")
        .update({
          quantity: newQuantity,
          total_price: newTotalPrice,
        })
        .eq("id", itemId);

      if (updateError) {
        console.error("Error updating item quantity:", updateError);
        toast.error("Erro ao atualizar quantidade");
        return false;
      }

      // Update order totals
      const { data: orderData } = await supabase
        .from("orders")
        .select("subtotal, total, discount, additional_fees, couvert_amount, payment_fee")
        .eq("id", orderId)
        .single();

      if (orderData) {
        const newSubtotal = Number(orderData.subtotal) - cancelledTotalPrice;
        const newTotal = newSubtotal - Number(orderData.discount || 0) + 
                        Number(orderData.additional_fees || 0) + 
                        Number(orderData.couvert_amount || 0) +
                        Number(orderData.payment_fee || 0);

        await supabase
          .from("orders")
          .update({ 
            subtotal: Math.max(0, newSubtotal), 
            total: Math.max(0, newTotal) 
          })
          .eq("id", orderId);
      }

      // Update local state
      setSentItems(prev => 
        prev.map(i => 
          i.id === itemId 
            ? { ...i, quantity: newQuantity, total_price: newTotalPrice }
            : i
        )
      );

      toast.success(`Quantidade de ${item.product_name} reduzida`);
      return true;
    } catch (error) {
      console.error("Error decreasing item quantity:", error);
      toast.error("Erro ao diminuir quantidade");
      return false;
    }
  }, [orderId, sentItems, activeRestaurant, cancelSentItem]);

  // Initial fetch + short retry loop to mitigate eventual consistency / trigger delays.
  useEffect(() => {
    let cancelled = false;

    if (retryTimeoutRef.current) {
      window.clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }

    retryAttemptRef.current = 0;

    const run = async () => {
      const found = await fetchOrderItems();
      if (cancelled) return;

      if (!found && (tableId || commandId) && retryAttemptRef.current < 5) {
        // If offline and cache didn't help, don't retry — network calls will fail anyway
        if (!isOnline) {
          console.log("[useCurrentOrderItems] Offline, stopping retry loop");
          return;
        }
        const delays = [400, 800, 1200, 1800, 2500];
        const delayMs = delays[retryAttemptRef.current] ?? 2500;
        retryAttemptRef.current += 1;
        retryTimeoutRef.current = window.setTimeout(run, delayMs);
      }
    };

    run();

    return () => {
      cancelled = true;
      if (retryTimeoutRef.current) {
        window.clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
    };
  }, [fetchOrderItems, tableId, commandId]);

  // If the table/command gets linked to an order after we entered (race), react immediately.
  useEffect(() => {
    if (!activeRestaurant) return;
    if (!isOnline) return; // Don't create Realtime subscriptions when offline

    if (tableId) {
      const channel = supabase
        .channel(`table-order-link-${tableId}`)
        .on(
          "postgres_changes",
          {
            event: "UPDATE",
            schema: "public",
            table: "tables",
            filter: `id=eq.${tableId}`,
          },
          (payload) => {
            const next = payload.new as { current_order_id?: string | null };
            if (next?.current_order_id && next.current_order_id !== orderId) {
              console.log(
                "[useCurrentOrderItems] Table current_order_id changed to new order, heavy refetch:",
                next.current_order_id
              );
              debouncedFetchOrderItems();
            } else if (next?.current_order_id && next.current_order_id === orderId) {
              // Same orderId - lightweight refetch of items only
              console.log("[useCurrentOrderItems] Table updated, same orderId - light refetch");
              debouncedRefetchItems();
            }
          }
        )
        .subscribe();

      return () => {
        safeRemoveChannel(channel);
      };
    }

    if (commandId) {
      const channel = supabase
        .channel(`command-order-link-${commandId}`)
        .on(
          "postgres_changes",
          {
            event: "UPDATE",
            schema: "public",
            table: "commands",
            filter: `id=eq.${commandId}`,
          },
          (payload) => {
            const next = payload.new as { current_order_id?: string | null };
            if (next?.current_order_id && next.current_order_id !== orderId) {
              console.log(
                "[useCurrentOrderItems] Command current_order_id changed to new order, heavy refetch:",
                next.current_order_id
              );
              debouncedFetchOrderItems();
            } else if (next?.current_order_id && next.current_order_id === orderId) {
              // Same orderId - lightweight refetch of items only
              console.log("[useCurrentOrderItems] Command updated, same orderId - light refetch");
              debouncedRefetchItems();
            }
          }
        )
        .subscribe();

      return () => {
        safeRemoveChannel(channel);
      };
    }
  }, [tableId, commandId, orderId, activeRestaurant, isOnline, debouncedFetchOrderItems, debouncedRefetchItems]);

  // While we don't have an orderId yet, listen for new/updated orders of this table/tab.
  useEffect(() => {
    if (!activeRestaurant) return;
    if (orderId) return;
    if (!isOnline) return; // Don't create subscriptions or make queries when offline

    let cancelled = false;
    let cleanup: undefined | (() => void);

    const setup = async () => {
      if (tableId) {
        const { data } = await supabase
          .from("tables")
          .select("number")
          .eq("id", tableId)
          .eq("restaurant_id", activeRestaurant.id)
          .maybeSingle();

        if (cancelled) return;
        if (!data?.number) return;

        const variations = getTableNumberVariations(data.number);
        const channel = supabase
          .channel(`orders-watch-table-${tableId}`)
          .on(
            "postgres_changes",
            {
              event: "*",
              schema: "public",
              table: "orders",
              filter: `restaurant_id=eq.${activeRestaurant.id}`,
            },
            (payload) => {
              const next = payload.new as { table_number?: string | null; status?: string | null };
              if (!next) return;
              if (!next.status || !ACTIVE_ORDER_STATUSES.includes(next.status as any)) return;
              if (next.table_number && variations.includes(next.table_number)) {
                console.log("[useCurrentOrderItems] Order change detected for table, refetching");
                debouncedFetchOrderItems();
              }
            }
          )
          .subscribe();

        cleanup = () => safeRemoveChannel(channel);
      }

      if (commandId) {
        const { data } = await supabase
          .from("commands")
          .select("number")
          .eq("id", commandId)
          .eq("restaurant_id", activeRestaurant.id)
          .maybeSingle();

        if (cancelled) return;
        if (!data?.number) return;

        const tabNumber = data.number;
        const channel = supabase
          .channel(`orders-watch-command-${commandId}`)
          .on(
            "postgres_changes",
            {
              event: "*",
              schema: "public",
              table: "orders",
              filter: `restaurant_id=eq.${activeRestaurant.id}`,
            },
            (payload) => {
              const next = payload.new as { tab_number?: string | null; status?: string | null };
              if (!next) return;
              if (!next.status || !ACTIVE_ORDER_STATUSES.includes(next.status as any)) return;
              if (next.tab_number && next.tab_number === tabNumber) {
                console.log("[useCurrentOrderItems] Order change detected for command, refetching");
                debouncedFetchOrderItems();
              }
            }
          )
          .subscribe();

        cleanup = () => safeRemoveChannel(channel);
      }
    };

    setup();

    return () => {
      cancelled = true;
      cleanup?.();
    };
  }, [orderId, tableId, commandId, activeRestaurant, isOnline, debouncedFetchOrderItems]);

  // Real-time subscription for order items
  // Instead of optimistic state updates (which can inject incorrect data from
  // Realtime filter leaks), we simply refetch from the database on any change.
  useEffect(() => {
    if (!orderId) return;
    if (!isOnline) return; // Don't create Realtime subscriptions when offline

    const channel = supabase
      .channel(`order-items-${orderId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'order_items',
          filter: `order_id=eq.${orderId}`
        },
        () => {
          // Lightweight refetch - only items, no order discovery
          debouncedRefetchItems();
        }
      )
      .subscribe();

    return () => {
      safeRemoveChannel(channel);
      // Clear any pending debounced refetch
      if (itemsDebounceRef.current) {
        window.clearTimeout(itemsDebounceRef.current);
        itemsDebounceRef.current = null;
      }
    };
  }, [orderId, isOnline, debouncedRefetchItems]);

  // Real-time subscription for cancellations
  useEffect(() => {
    if (!orderId) return;
    if (!isOnline) return; // Don't create Realtime subscriptions when offline

    const channel = supabase
      .channel(`order-cancellations-${orderId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'order_item_cancellations',
          filter: `order_id=eq.${orderId}`
        },
        (payload) => {
          const newCancellation = payload.new as CancelledOrderItem;
          setCancelledItems(prev => [...prev, newCancellation]);
        }
      )
      .subscribe();

    return () => {
      safeRemoveChannel(channel);
    };
  }, [orderId, isOnline]);

  // Listener específico para detectar fechamento externo da mesa atualmente aberta
  // (ex.: pagamento processado em outro dispositivo, como o APK do garçom).
  // Quando tables.status muda para 'available' e current_order_id vira null,
  // forçamos a limpeza explícita do estado para evitar "consumo fantasma"
  // mesmo com o fail-safe (linhas 372-376) preservando sentItems.
  // Aplica-se SOMENTE ao modo mesa (tableId). Comandas e balcão não criam este listener.
  useEffect(() => {
    if (!tableId) return;
    if (!isOnline) return;

    const tableCleanupChannel = supabase
      .channel(`table-closed-external-${tableId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'tables',
          filter: `id=eq.${tableId}`,
        },
        (payload) => {
          const updatedTable = payload.new as { status?: string | null; current_order_id?: string | null };
          if (
            updatedTable.status === 'available' &&
            updatedTable.current_order_id === null
          ) {
            const sinceOnline = Date.now() - getLastOnlineTransitionAt();
            if (sinceOnline < RECONNECT_GRACE_MS) {
              console.log(
                `[useCurrentOrderItems] Realtime close ignored (${sinceOnline}ms after reconnect)`
              );
              return;
            }
            console.log('[useCurrentOrderItems] Mesa fechada externamente, limpando itens');
            hadItemsRef.current = false; // reset fail-safe
            setSentItems([]);
            setCancelledItems([]);
            setOrderId(null);
            setOrderNumber(null);
          }
        }
      )
      .subscribe();

    return () => {
      safeRemoveChannel(tableCleanupChannel);
    };
  }, [tableId, isOnline]);

  return {
    sentItems,
    cancelledItems,
    isLoading,
    orderId,
    orderNumber,
    isCancelling,
    fetchError,
    isUsingCache,
    isOnline,
    lastSuccessfulFetch: lastSuccessfulFetchRef.current,
    refetch: fetchOrderItems,
    cancelSentItem,
    decreaseItemQuantity,
  };
}
